import { useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useState } from "react";
import type { HubOverview } from "../../shared/tool-hubs";
import { Button } from "../components/ui/button";
import { ContentLoading } from "../components/ContentLoading";
import { FieldError } from "../components/ui/field";
import { Sheet } from "../components/ui/sheet";
import { documentDate } from "../knowledge/customer-document-format";
import { contractLine } from "./customer-page-format";
import { HubEditForm } from "./HubEditForm";
import { OperationsLink } from "./OperationsLink";
import type { OperationsNavigate } from "./navigation";
import { useHubOverview } from "./customer-page-queries";

/** One tool: its go-live, contract, deployment, and the single next step (ADR-0086). */
export function HubCard({
  overview,
  customerSlug,
  navigate,
}: {
  overview: HubOverview;
  customerSlug: string;
  navigate: OperationsNavigate;
}) {
  const { hub, next } = overview;
  const contract = contractLine(overview.documents);
  const due = next.rule !== "nothing_due";
  return (
    <OperationsLink
      className="group block rounded-xl bg-card p-6 no-underline shadow-card"
      href={`/customers/${customerSlug}/tools/${hub.id}`}
      navigate={navigate}
    >
      <span className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <span className="text-lg font-bold text-foreground group-hover:text-link">{hub.name}</span>
        <span className="text-sm text-muted-foreground">
          {hub.goLiveOn ? `Go-live ${documentDate(hub.goLiveOn)}` : "No go-live date"}
        </span>
      </span>
      <span className="mt-1 block text-muted-foreground">
        {[contract, hub.deployed ? "Deployed" : "Not deployed yet"].filter(Boolean).join(" · ")}
      </span>
      <span className={`mt-3 block font-semibold ${due ? "text-link" : "text-muted-foreground"}`}>
        {due ? `Next: ${next.text}` : "Nothing due"}
      </span>
    </OperationsLink>
  );
}

export function CustomerHubs({
  organizationId,
  customerSlug,
  customerName,
  canRead,
  canWrite,
  navigate,
}: {
  organizationId: string;
  customerSlug: string;
  customerName: string;
  canRead: boolean;
  canWrite: boolean;
  navigate: OperationsNavigate;
}) {
  const [creating, setCreating] = useState(false);
  const overview = useHubOverview(organizationId, canRead);
  if (!canRead)
    return <FieldError>Your employee permissions do not include customer context.</FieldError>;
  const hubs = overview.data?.hubs ?? [];
  return (
    <section className="space-y-5" aria-labelledby="customer-tools-title">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 id="customer-tools-title" className="text-xl font-bold">
            Tools
          </h2>
          <p className="mt-1 text-muted-foreground">
            One place for each thing {customerName} buys, from the first call onward.
          </p>
        </div>
        {canWrite ? (
          <Button
            onClick={() => {
              setCreating(true);
            }}
          >
            <Plus /> New tool
          </Button>
        ) : null}
      </div>
      {overview.isPending ? <ContentLoading label="Loading tools…" variant="cards" /> : null}
      {overview.error ? <FieldError>{overview.error.message}</FieldError> : null}
      {overview.data && !hubs.length ? (
        <p className="rounded-xl bg-card p-8 text-center text-muted-foreground shadow-card">
          No tools yet. Add one as soon as you start scoping it; its order form, calls, and files
          collect there.
        </p>
      ) : null}
      <div className="grid gap-4 xl:grid-cols-2">
        {hubs.map((item) => (
          <HubCard
            key={item.hub.id}
            overview={item}
            customerSlug={customerSlug}
            navigate={navigate}
          />
        ))}
      </div>
      <NewToolSheet
        open={creating}
        organizationId={organizationId}
        customerSlug={customerSlug}
        customerName={customerName}
        navigate={navigate}
        onClose={() => {
          setCreating(false);
        }}
      />
    </section>
  );
}

function NewToolSheet({
  open,
  organizationId,
  customerSlug,
  customerName,
  navigate,
  onClose,
}: {
  open: boolean;
  organizationId: string;
  customerSlug: string;
  customerName: string;
  navigate: OperationsNavigate;
  onClose: () => void;
}) {
  const client = useQueryClient();
  return (
    <Sheet open={open} title="New tool" kicker={customerName} onClose={onClose}>
      <HubEditForm
        organizationId={organizationId}
        onCancel={onClose}
        onDone={(hub) => {
          onClose();
          void client.invalidateQueries({ queryKey: ["ops", "tool-hubs", organizationId] });
          navigate(`/customers/${customerSlug}/tools/${hub.id}`);
        }}
      />
    </Sheet>
  );
}
